import { ReactNode, useMemo, useState } from "react";
import _throttle from "lodash/throttle";

import { useEffectOnce } from "shared/lib/hooks";

import { FrequentlyChangingCallingContext } from "./default-context";
import { FrequentlyChangingCallingContextI } from "@/lib/types/contexts/calling";
import { CUSTOM_EVENTS } from "@/constants/custom-events";
import { dispatchCustomEvent } from "@/helpers/events";

interface FrequentlyChangingCallingContextProviderPropsI {
  children: ReactNode;
  context: FrequentlyChangingCallingContextI;
}

export const FrequentlyChangingCallingContextEventDispatcher = {
  dispatch: {
    inputVolUpdated: (inputVol: number) => {
      dispatchCustomEvent<number>({
        eventType:
          CUSTOM_EVENTS.CALLING_MODULE.FREQUENTLY_CHANGING_CONTEXT
            .INPUT_VOLUME_UPDATED,
        data: inputVol,
      });
    },

    outputVolUpdated: (outputVol: number) => {
      dispatchCustomEvent<number>({
        eventType:
          CUSTOM_EVENTS.CALLING_MODULE.FREQUENTLY_CHANGING_CONTEXT
            .OUTPUT_VOLUME_UPDATED,
        data: outputVol,
      });
    },

    callQualityUpdated: (callQuality: number) => {
      dispatchCustomEvent<number>({
        eventType:
          CUSTOM_EVENTS.CALLING_MODULE.FREQUENTLY_CHANGING_CONTEXT
            .CALL_QUALITY_UPDATED,
        data: callQuality,
      });
    },
  },
};

export const CallingFrequentlyChangingContentProvider = ({
  children,
  context,
}: FrequentlyChangingCallingContextProviderPropsI) => {
  // Define set functions
  const [inputVol, setInputVol] = useState(context.inputVol);
  const [outputVol, setOutputVol] = useState(context.outputVol);
  const [callQuality, setCallQuality] = useState(context.callQuality);

  const throttledSetInputVol = useMemo(() => _throttle(setInputVol, 300), []);
  const throttledSetOutputVol = useMemo(() => _throttle(setOutputVol, 300), []);

  useEffectOnce(() => {
    const handleInputVol = (inputVol: any) => setInputVol(inputVol.detail);
    const handleOutputVol = (outputVol: any) => setOutputVol(outputVol.detail);
    const handleCallQuality = (callQuality: any) => setCallQuality(callQuality);

    window.document.addEventListener(
      CUSTOM_EVENTS.CALLING_MODULE.FREQUENTLY_CHANGING_CONTEXT
        .INPUT_VOLUME_UPDATED,
      handleInputVol
    );

    window.document.addEventListener(
      CUSTOM_EVENTS.CALLING_MODULE.FREQUENTLY_CHANGING_CONTEXT
        .OUTPUT_VOLUME_UPDATED,
      handleOutputVol
    );

    window.document.addEventListener(
      CUSTOM_EVENTS.CALLING_MODULE.FREQUENTLY_CHANGING_CONTEXT
        .CALL_QUALITY_UPDATED,
      handleCallQuality
    );

    return () => {
      window.document.removeEventListener(
        CUSTOM_EVENTS.CALLING_MODULE.FREQUENTLY_CHANGING_CONTEXT
          .INPUT_VOLUME_UPDATED,
        handleInputVol
      );

      window.document.removeEventListener(
        CUSTOM_EVENTS.CALLING_MODULE.FREQUENTLY_CHANGING_CONTEXT
          .OUTPUT_VOLUME_UPDATED,
        handleOutputVol
      );

      window.document.removeEventListener(
        CUSTOM_EVENTS.CALLING_MODULE.FREQUENTLY_CHANGING_CONTEXT
          .CALL_QUALITY_UPDATED,
        handleCallQuality
      );
    };
  });

  const contextValue = useMemo(
    () => ({
      inputVol,
      outputVol,
      callQuality,
      setCallQuality,
      setInputVol: throttledSetInputVol,
      setOutputVol: throttledSetOutputVol,
    }),
    [inputVol, outputVol, callQuality]
  );

  return (
    <FrequentlyChangingCallingContext.Provider
      value={contextValue as FrequentlyChangingCallingContextI}
    >
      {children}
    </FrequentlyChangingCallingContext.Provider>
  );
};

export default CallingFrequentlyChangingContentProvider;
